import { apiGet, apiPost, apiPut, apiDelete } from './api';

/**
 * Service for managing application settings via API calls
 */
export const settingsService = {
  // ==================== USER SETTINGS ====================
  
  /**
   * Get all user settings
   */
  async getUserSettings(userId = null) {
    const endpoint = userId ? `/settings/user?userId=${userId}` : '/settings/user';
    return apiGet(endpoint);
  },

  /**
   * Get user settings by category
   */
  async getUserSettingsByCategory(category, userId = null) {
    const endpoint = userId ? `/settings/user/${category}?userId=${userId}` : `/settings/user/${category}`;
    return apiGet(endpoint);
  },

  /**
   * Get a specific user setting
   */
  async getUserSetting(category, key, userId = null) {
    const endpoint = userId ? `/settings/user/${category}/${key}?userId=${userId}` : `/settings/user/${category}/${key}`;
    return apiGet(endpoint);
  },

  /**
   * Update a specific user setting
   */
  async updateUserSetting(category, key, value, userId = null) {
    const endpoint = userId ? `/settings/user/${category}/${key}?userId=${userId}` : `/settings/user/${category}/${key}`;
    return apiPut(endpoint, value);
  },

  /**
   * Bulk update user settings in a category
   */
  async updateSettingsBulk(category, settings, replaceAll = false, userId = null) {
    const endpoint = userId ? `/settings/user/${category}?userId=${userId}` : `/settings/user/${category}`;
    const data = {
      settings: settings,
      replaceAll: replaceAll
    };
    return apiPut(endpoint, data);
  },

  /**
   * Delete a user setting
   */
  async deleteUserSetting(category, key, userId = null) {
    const endpoint = userId ? `/settings/user/${category}/${key}?userId=${userId}` : `/settings/user/${category}/${key}`;
    return apiDelete(endpoint);
  },

  // ==================== EFFECTIVE SETTINGS ====================
  
  /**
   * Get effective settings for user (user overrides + system defaults)
   */
  async getEffectiveSettings(userId = null) {
    const endpoint = userId ? `/settings/effective?userId=${userId}` : '/settings/effective';
    return apiGet(endpoint);
  },

  // ==================== SYSTEM SETTINGS ====================
  
  /**
   * Get all public system settings
   */
  async getSystemSettings() {
    return apiGet('/settings/system');
  },

  /**
   * Get system settings by category
   */
  async getSystemSettingsByCategory(category, includePrivate = false) {
    const endpoint = `/settings/system/${category}?includePrivate=${includePrivate}`;
    return apiGet(endpoint);
  },

  // ==================== TEMPLATES ====================
  
  /**
   * Get accessible templates for user
   */
  async getTemplates(userId = null) {
    const endpoint = userId ? `/settings/templates?userId=${userId}` : '/settings/templates';
    return apiGet(endpoint);
  },

  /**
   * Apply a settings template
   */
  async applyTemplate(templateId, userId = null) {
    const endpoint = userId ? `/settings/templates/${templateId}/apply?userId=${userId}` : `/settings/templates/${templateId}/apply`;
    return apiPost(endpoint, {});
  },

  // ==================== EXPORT/IMPORT ====================
  
  /**
   * Export user settings
   */
  async exportSettings(userId = null) {
    const endpoint = userId ? `/settings/export?userId=${userId}` : '/settings/export';
    return apiGet(endpoint);
  },

  /**
   * Import user settings
   */
  async importSettings(settingsData, userId = null) {
    const endpoint = userId ? `/settings/import?userId=${userId}` : '/settings/import';
    return apiPost(endpoint, settingsData);
  },

  // ==================== VALIDATION ====================
  
  /**
   * Validate settings before saving
   */
  async validateSettings(category, settings) {
    const endpoint = `/settings/validate?category=${category}`;
    return apiPost(endpoint, settings);
  },

  // ==================== UTILITY METHODS ====================
  
  /**
   * Get setting value with fallback
   */
  getSettingValue(settings, category, key, defaultValue = null) {
    return settings?.[category]?.[key] ?? defaultValue;
  },

  /**
   * Check if setting exists
   */
  hasSetting(settings, category, key) {
    return settings?.[category]?.hasOwnProperty(key) ?? false;
  },

  /**
   * Get all settings for a category
   */
  getCategorySettings(settings, category) {
    return settings?.[category] ?? {};
  },

  /**
   * Merge settings with defaults
   */
  mergeWithDefaults(userSettings, systemDefaults) {
    const merged = { ...systemDefaults };
    
    Object.keys(userSettings).forEach(category => {
      merged[category] = {
        ...merged[category],
        ...userSettings[category]
      };
    });
    
    return merged;
  },

  /**
   * Format settings for display
   */
  formatSettingValue(value, type = 'auto') {
    if (value === null || value === undefined) {
      return 'Not set';
    }
    
    switch (type) {
      case 'boolean':
        return value ? 'Enabled' : 'Disabled';
      case 'number':
        return value.toLocaleString();
      case 'duration':
        return this.formatDuration(value);
      case 'bytes':
        return this.formatBytes(value);
      default:
        return String(value);
    }
  },

  /**
   * Format duration in seconds to human readable
   */
  formatDuration(seconds) {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      return `${Math.floor(seconds / 60)}m`;
    } else {
      return `${Math.floor(seconds / 3600)}h`;
    }
  },

  /**
   * Format bytes to human readable
   */
  formatBytes(bytes) {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)}${units[unitIndex]}`;
  },

  /**
   * Get setting validation rules
   */
  getValidationRules() {
    return {
      general: {
        theme: { type: 'select', options: ['light', 'dark'] },
        language: { type: 'select', options: ['en', 'es', 'fr', 'de'] },
        timezone: { type: 'timezone' },
        session_timeout: { type: 'number', min: 300, max: 86400 },
        auto_save_interval: { type: 'number', min: 10, max: 300 }
      },
      pipeline: {
        default_retry_count: { type: 'number', min: 0, max: 10 },
        default_timeout: { type: 'number', min: 60, max: 7200 },
        log_level: { type: 'select', options: ['DEBUG', 'INFO', 'WARN', 'ERROR'] },
        auto_refresh_interval: { type: 'number', min: 1, max: 60 }
      },
      connections: {
        default_connection_timeout: { type: 'number', min: 5, max: 300 },
        default_pool_size: { type: 'number', min: 1, max: 100 },
        health_check_interval: { type: 'number', min: 30, max: 3600 }
      },
      notifications: {
        pipeline_success_notifications: { type: 'boolean' },
        pipeline_failure_notifications: { type: 'boolean' },
        email_notifications: { type: 'boolean' },
        in_app_notifications: { type: 'boolean' }
      }
    };
  },

  /**
   * Validate setting value locally
   */
  validateSettingValue(category, key, value) {
    const rules = this.getValidationRules();
    const rule = rules[category]?.[key];
    
    if (!rule) {
      return { valid: true };
    }
    
    switch (rule.type) {
      case 'number':
        if (typeof value !== 'number') {
          return { valid: false, error: 'Must be a number' };
        }
        if (rule.min !== undefined && value < rule.min) {
          return { valid: false, error: `Must be at least ${rule.min}` };
        }
        if (rule.max !== undefined && value > rule.max) {
          return { valid: false, error: `Must be at most ${rule.max}` };
        }
        break;
        
      case 'select':
        if (!rule.options.includes(value)) {
          return { valid: false, error: `Must be one of: ${rule.options.join(', ')}` };
        }
        break;
        
      case 'boolean':
        if (typeof value !== 'boolean') {
          return { valid: false, error: 'Must be true or false' };
        }
        break;
    }
    
    return { valid: true };
  }
};

export default settingsService;